import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { BaseResponse } from '../core/models/responses/base-response';
import { BasePagingRequest } from '../core/models/requests/base-paging-request';
import { BasePagingResponse } from '../core/models/responses/base-paging-response';
import { PosDeviceProviderType } from '../models/enums/pos-enums';
import {
  PosConnectionResult,
  PosDevice,
  PosDeviceSaveRequest,
  PosIntegrationSettingRequest,
  PosIntegrationSettingView,
  PosSaleOptions,
  PosSalePreview,
  PosSaleStartRequest,
  PosTerminalAssignRequest,
  PosTransaction,
  PosTransactionFilter,
  PosTransactionSummary,
} from '../models/pos/pos-device';
import { EInvoiceApiService } from './e-invoice-api.service';

/**
 * POS cihaz entegrasyonu uçları (arka uç: PosControllers, pos-entegrasyonu.md).
 *
 * EInvoiceApiService tabanı bilerek kullanılıyor: POS uçları da sağlayıcı ve iş
 * kuralı hatalarını 400 + { isSuccess: false, message } ile döndürüyor; global hata
 * işleyicisi bu mesajı yutardı.
 */

@Injectable({ providedIn: 'root' })
export class PosIntegrationSettingService extends EInvoiceApiService {
  constructor(http: HttpClient) {
    super(http, environment.urlAddress + '/PosIntegrationSetting');
  }

  get(providerType: PosDeviceProviderType): Observable<BaseResponse<PosIntegrationSettingView>> {
    return this.getRequest<BaseResponse<PosIntegrationSettingView>>(`${providerType}`);
  }

  save(request: PosIntegrationSettingRequest): Observable<BaseResponse<PosIntegrationSettingView>> {
    return this.postRequest<BaseResponse<PosIntegrationSettingView>>('', request);
  }

  /** Entegratör kimliğini doğrular; cihaza dokunmaz. */
  test(providerType: PosDeviceProviderType): Observable<BaseResponse<PosConnectionResult>> {
    return this.postRequest<BaseResponse<PosConnectionResult>>(`test/${providerType}`);
  }
}

@Injectable({ providedIn: 'root' })
export class PosDeviceService extends EInvoiceApiService {
  constructor(http: HttpClient) {
    super(http, environment.urlAddress + '/PosDevice');
  }

  list(onlyActive = false): Observable<BaseResponse<PosDevice[]>> {
    return this.getRequest<BaseResponse<PosDevice[]>>('', new HttpParams().set('onlyActive', String(onlyActive)));
  }

  save(request: PosDeviceSaveRequest): Observable<BaseResponse<PosDevice>> {
    return this.postRequest<BaseResponse<PosDevice>>('', request);
  }

  delete(id: number): Observable<BaseResponse<{ id: number }>> {
    return this.deleteRequest<BaseResponse<{ id: number }>>(`${id}`);
  }

  /** Cihaza özel sağlayıcı kimliğini (yeniden) üretir. */
  provision(id: number): Observable<BaseResponse<PosDevice>> {
    return this.postRequest<BaseResponse<PosDevice>>(`${id}/provision`);
  }

  /** Eşleştirmeyi başlatır; dönen kod cihaz ekranında onaylanır. */
  startPairing(id: number): Observable<BaseResponse<PosDevice>> {
    return this.postRequest<BaseResponse<PosDevice>>(`${id}/pairing`);
  }

  /**
   * Yeni kaynak kimliğiyle yeni eşleştirme kodu üretir. Sağlayıcı aynı kimliğe bekleyen talep
   * varken yenisini açmadığı için, cihaza düşmeyen ya da takılan talepten çıkış yolu budur.
   * Mevcut eşleştirme geçersizleşir.
   */
  renewPairing(id: number): Observable<BaseResponse<PosDevice>> {
    return this.postRequest<BaseResponse<PosDevice>>(`${id}/pairing/renew`);
  }

  refreshPairing(id: number): Observable<BaseResponse<PosDevice>> {
    return this.postRequest<BaseResponse<PosDevice>>(`${id}/pairing/refresh`);
  }

  /** POS kasasını cihaza bağlar; posDeviceId boşsa bağı kaldırır. */
  assignTerminal(request: PosTerminalAssignRequest): Observable<BaseResponse<unknown>> {
    return this.postRequest<BaseResponse<unknown>>('terminal', request);
  }
}

@Injectable({ providedIn: 'root' })
export class PosTransactionService extends EInvoiceApiService {
  constructor(http: HttpClient) {
    super(http, environment.urlAddress + '/PosTransaction');
  }

  /** Hızlı satışın POS seçenekleri (nakdin cihaza gönderimi). */
  options(): Observable<BaseResponse<PosSaleOptions>> {
    return this.getRequest<BaseResponse<PosSaleOptions>>('options');
  }

  /** Cihaza gidecek belgenin satırları — gönderimden önce gösterilir. */
  preview(request: PosSaleStartRequest): Observable<BaseResponse<PosSalePreview>> {
    return this.postRequest<BaseResponse<PosSalePreview>>('preview', request);
  }

  /** Kaydı yazar ve cihaza gönderir; sonuç refresh ile yoklanır. */
  start(request: PosSaleStartRequest): Observable<BaseResponse<PosTransaction>> {
    return this.postRequest<BaseResponse<PosTransaction>>('', request);
  }

  list(filter: PosTransactionFilter): Observable<BaseResponse<PosTransaction[]>> {
    let params = new HttpParams();
    Object.entries(filter || {}).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') params = params.set(key, String(value));
    });
    return this.getRequest<BaseResponse<PosTransaction[]>>('', params);
  }

  /** POS İşlemleri ekranı — sayfalı liste. */
  listPaged(request: BasePagingRequest<PosTransactionFilter>): Observable<BasePagingResponse<PosTransaction>> {
    return this.postRequest<BasePagingResponse<PosTransaction>>('list', request);
  }

  /** POS İşlemleri ekranının KPI'ları — dönem ve cihaz süzgecine göre. */
  summary(filter: PosTransactionFilter): Observable<BaseResponse<PosTransactionSummary>> {
    return this.postRequest<BaseResponse<PosTransactionSummary>>('summary', filter);
  }

  /** Ayrıntı — ham istek/cevap dahil. */
  get(id: number): Observable<BaseResponse<PosTransaction>> {
    return this.getRequest<BaseResponse<PosTransaction>>(`${id}`);
  }

  /** Sağlayıcıdan güncel durumu okur. */
  refresh(id: number): Observable<BaseResponse<PosTransaction>> {
    return this.postRequest<BaseResponse<PosTransaction>>(`${id}/refresh`);
  }

  /** Cihazda bekleyen isteği geri çeker; iptal teyit edilene kadar işlem açık kalır. */
  cancel(id: number): Observable<BaseResponse<PosTransaction>> {
    return this.postRequest<BaseResponse<PosTransaction>>(`${id}/cancel`);
  }

  /** Onaylı satışı cihazdan iptal eder; dönen yeni iptal işlemidir. */
  void(id: number): Observable<BaseResponse<PosTransaction>> {
    return this.postRequest<BaseResponse<PosTransaction>>(`${id}/void`);
  }

  /** Onaylı satıştan seçilen satırları cihazdan iade eder; dönen yeni iade işlemidir (K24). */
  refund(id: number, lineNos: number[]): Observable<BaseResponse<PosTransaction>> {
    return this.postRequest<BaseResponse<PosTransaction>>(`${id}/refund`, { lineNos });
  }
}
